import { z } from 'zod';
/**
 * Billing rules, defined once (Groundly Pricing & Billing Spec, sections 2-3,
 * with the user's changes: one invoice per account, a cycle starting on the
 * 5th of each month, and a prorated first charge for a website whose plan
 * starts part-way through a cycle). Money is integer US cents everywhere.
 * Plans are per website. The assistant never stops or degrades at a plan's
 * cap: usage only changes what the next invoice charges.
 */
export const PLAN_IDS = ['free', 'starter', 'growth', 'pro'];
export const planIdSchema = z.enum(PLAN_IDS);
export const PRICING_TYPES = ['founding', 'list'];
export const pricingTypeSchema = z.enum(PRICING_TYPES);
/**
 * How a website pays for its plan: every month, or once a year up front at
 * 10 × the monthly price (2 months free). Billing cycles stay monthly either
 * way, and so do the plan's AI replies and page approvals.
 */
export const BILLING_INTERVALS = ['monthly', 'yearly'];
export const billingIntervalSchema = z.enum(BILLING_INTERVALS);
/** A year of a yearly plan, in billing cycles. */
export const CYCLES_PER_YEAR = 12;
/** Remind the account this many days before a yearly plan renews. */
export const RENEWAL_NOTICE_DAYS = 30;
/**
 * A customer who cancels a new yearly plan within this many days of paying
 * for it may ask for that payment back in full; Groundly checks each request
 * before refunding it, and an approved one ends the plan at once (user,
 * 2026-11-01; Refund & Cancellation Policy, section 5).
 */
export const YEARLY_REFUND_DAYS = 14;
/** A 14-day yearly refund request: waiting for Groundly, approved, or declined. */
export const REFUND_REQUEST_STATUSES = ['pending', 'approved', 'declined'];
export const refundRequestStatusSchema = z.enum(REFUND_REQUEST_STATUSES);
/** An invoice is due this many days after it is issued (terms, section 5). */
export const INVOICE_DUE_DAYS = 7;
/**
 * Reminders for an unpaid invoice go out this many days before it is due.
 * Once its due date has passed, every service on the account stops until it
 * is paid, with no grace period, and comes back as soon as it is (user,
 * 2026-11-01).
 */
export const INVOICE_REMINDER_DAYS = [3, 2, 1];
export const INVOICE_STATUSES = ['unpaid', 'paid', 'void'];
export const invoiceStatusSchema = z.enum(INVOICE_STATUSES);
/**
 * A discount code as typed: any case, kept in capitals. Codes are created by
 * Groundly and redeemed by any account on one of its websites.
 */
export const discountCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9_-]{3,32}$/, 'A discount code is 3 to 32 letters, digits, - or _.');
/** A discount takes a percentage off, or a fixed amount in cents. */
export const DISCOUNT_KINDS = ['percent', 'amount'];
export const discountKindSchema = z.enum(DISCOUNT_KINDS);
/**
 * Section 2. The `plans` table is seeded from these values; after that the
 * table is the source of truth. Prices and allowances are read from it
 * everywhere (dashboard and invoices), so a price changed there applies
 * without a release. These stay as the seed and as defaults for tests.
 */
export const PLANS = [
    {
        id: 'free',
        name: 'Free',
        includedMessages: 100,
        pagesPerCycle: 20,
        customPages: 0,
        stopsAtIncluded: true,
        listPriceCents: 0,
        foundingPriceCents: 0,
        listYearlyPriceCents: 0,
        foundingYearlyPriceCents: 0,
        overageCentsPerReply: 0,
        integrations: false,
    },
    {
        id: 'starter',
        name: 'Starter',
        includedMessages: 2_000,
        pagesPerCycle: 200,
        customPages: 20,
        stopsAtIncluded: false,
        listPriceCents: 4_900,
        foundingPriceCents: 3_900,
        listYearlyPriceCents: 49_000,
        foundingYearlyPriceCents: 39_000,
        overageCentsPerReply: 2,
        integrations: true,
    },
    {
        id: 'growth',
        name: 'Growth',
        includedMessages: 6_000,
        pagesPerCycle: 600,
        customPages: 60,
        stopsAtIncluded: false,
        listPriceCents: 9_900,
        foundingPriceCents: 7_900,
        listYearlyPriceCents: 99_000,
        foundingYearlyPriceCents: 79_000,
        overageCentsPerReply: 2,
        integrations: true,
    },
    {
        id: 'pro',
        name: 'Pro',
        includedMessages: 20_000,
        pagesPerCycle: 2_000,
        customPages: 200,
        stopsAtIncluded: false,
        listPriceCents: 24_900,
        foundingPriceCents: 19_900,
        listYearlyPriceCents: 249_000,
        foundingYearlyPriceCents: 199_000,
        overageCentsPerReply: 2,
        integrations: true,
    },
];
/**
 * Every AI reply over a paid plan's included amount costs 2¢, billed exactly:
 * no blocks, no rounding up and no ceiling (user, 2026-11-01, replacing $50
 * blocks of 1,000). When a bigger plan would cost less, the dashboard suggests
 * it (`usageWarnings`). The `plans` table holds each plan's rate; this is the
 * seed's.
 */
export const OVERAGE_CENTS_PER_REPLY = 2;
export function planById(id) {
    const plan = PLANS.find((p) => p.id === id);
    if (!plan)
        throw new Error(`unknown plan ${id}`);
    return plan;
}
/** The next plan up in `plans` (cheapest first), or `null` at the top. */
export function nextPlanUp(id, plans = PLANS) {
    const index = plans.findIndex((p) => p.id === id);
    return index === -1 ? null : (plans[index + 1] ?? null);
}
// ---------------------------------------------------------------------------
// Billing cycles: the 5th of one month to the 4th of the next, in UTC
// ---------------------------------------------------------------------------
/** Every billing cycle starts on this day of the month. */
export const BILLING_DAY = 5;
const DAY_MS = 86_400_000;
function utcDate(iso) {
    return Date.parse(`${iso}T00:00:00Z`);
}
function isoOf(ms) {
    return new Date(ms).toISOString().slice(0, 10);
}
/** The cycle `at` falls in: from the 5th on or before it to the 4th after. */
export function periodOf(at) {
    const y = at.getUTCFullYear();
    const m = at.getUTCMonth() - (at.getUTCDate() < BILLING_DAY ? 1 : 0);
    return {
        start: isoOf(Date.UTC(y, m, BILLING_DAY)),
        end: isoOf(Date.UTC(y, m + 1, BILLING_DAY - 1)),
    };
}
/** The cycle containing a `YYYY-MM-DD` date. */
export function periodOfDate(isoDate) {
    return periodOf(new Date(utcDate(isoDate)));
}
/** The cycle after `period`. */
export function nextPeriod(period) {
    return periodOf(new Date(utcDate(period.end) + DAY_MS));
}
/** Days from `from` to `to`, both included. */
export function daysInclusive(from, to) {
    return Math.round((utcDate(to) - utcDate(from)) / DAY_MS) + 1;
}
export function daysInPeriod(period) {
    return daysInclusive(period.start, period.end);
}
/** The day after a `YYYY-MM-DD` date. */
export function addDays(isoDate, days) {
    return isoOf(utcDate(isoDate) + days * DAY_MS);
}
/** Whole months from one cycle start to another (both the 5th). */
export function cyclesBetween(fromPeriodStart, toPeriodStart) {
    const [fy, fm] = fromPeriodStart.split('-').map(Number);
    const [ty, tm] = toPeriodStart.split('-').map(Number);
    return (ty - fy) * 12 + (tm - fm);
}
/** The cycle start `cycles` cycles after `periodStart` (a 5th). */
export function addCycles(periodStart, cycles) {
    const [y, m] = periodStart.split('-').map(Number);
    return isoOf(Date.UTC(y, m - 1 + cycles, BILLING_DAY));
}
// ---------------------------------------------------------------------------
// Yearly plans: the fee is charged in the cycle a year starts in, every 12 cycles
// ---------------------------------------------------------------------------
/**
 * Whether a yearly plan that started on `planStartsOn` (always a cycle start)
 * charges its yearly fee in the cycle starting `periodStart`: its first cycle
 * and every 12th after.
 */
export function yearlyFeeDue(planStartsOn, periodStart) {
    const cycles = cyclesBetween(planStartsOn, periodStart);
    return cycles >= 0 && cycles % CYCLES_PER_YEAR === 0;
}
/** The first cycle start after `periodStart` in which a yearly plan's fee comes due again. */
export function nextRenewal(planStartsOn, periodStart) {
    const cycles = Math.max(0, cyclesBetween(planStartsOn, periodStart));
    return addCycles(planStartsOn, (Math.floor(cycles / CYCLES_PER_YEAR) + 1) * CYCLES_PER_YEAR);
}
/**
 * For a yearly plan replaced at the start of `periodStart`: the cycle its
 * current year's fee was charged in, and how many of that year's 12 cycles
 * are left unused (0 when `periodStart` would have renewed it anyway).
 */
export function unusedYear(planStartsOn, periodStart) {
    const cycles = Math.max(0, cyclesBetween(planStartsOn, periodStart));
    const used = cycles % CYCLES_PER_YEAR;
    return {
        feeCycleStart: addCycles(planStartsOn, cycles - used),
        unusedCycles: used === 0 ? 0 : CYCLES_PER_YEAR - used,
    };
}
/** The credit for the unused cycles of a yearly fee: `fee × unused ÷ 12`, rounded. */
export function unusedYearCredit(feeCents, unusedCycles) {
    return Math.round((Math.max(0, feeCents) * unusedCycles) / CYCLES_PER_YEAR);
}
/**
 * The part of a cycle a plan is billed for: the whole cycle, or from the
 * plan's first day when it starts part-way through (a website added
 * mid-cycle). Later cycles are always whole.
 */
export function billedWindow(period, planStartsOn) {
    return { start: planStartsOn > period.start ? planStartsOn : period.start, end: period.end };
}
// ---------------------------------------------------------------------------
// Prices and amounts (section 3.4)
// ---------------------------------------------------------------------------
/**
 * Whether founding pricing applies: the plan is on founding pricing, which
 * lasts for life (user, 2026-11-01; it was locked for 12 months). Groundly
 * ends it only by moving the website to list pricing.
 */
export function foundingApplies(pricingType) {
    return pricingType === 'founding';
}
/** The monthly price at founding or list pricing. */
export function basePriceCents(plan, founding) {
    return founding ? plan.foundingPriceCents : plan.listPriceCents;
}
/** The yearly price at founding or list pricing. */
export function yearlyPriceCents(plan, founding) {
    return founding ? plan.foundingYearlyPriceCents : plan.listYearlyPriceCents;
}
/** `value × billed days ÷ cycle days`, rounded half up to a whole number. */
export function prorate(value, billedDays, periodDays) {
    if (billedDays >= periodDays)
        return value;
    return Math.round((value * billedDays) / periodDays);
}
/**
 * One website's charge for a cycle. Overage is `max(0, used − included)` ×
 * the plan's per-reply rate, never capped (user, 2026-11-01): every extra AI
 * reply is charged, and when a bigger plan would cost less the dashboard
 * suggests it (`usageWarnings`). The yearly fee is charged only in the cycle
 * it is due. Zero usage still pays the price.
 */
export function lineAmounts(plan, founding, messagesUsed, options = {}) {
    const billedDays = options.billedDays ?? 1;
    const periodDays = options.periodDays ?? 1;
    const yearly = options.interval === 'yearly';
    const includedMessages = prorate(plan.includedMessages, billedDays, periodDays);
    const baseCents = yearly
        ? options.yearlyFeeDue === true
            ? yearlyPriceCents(plan, founding)
            : 0
        : prorate(basePriceCents(plan, founding), billedDays, periodDays);
    // A plan that stops at its included replies never has overage to charge.
    const overageMessages = plan.stopsAtIncluded ? 0 : Math.max(0, messagesUsed - includedMessages);
    const overageCents = overageMessages * plan.overageCentsPerReply;
    return {
        includedMessages,
        baseCents,
        overageMessages,
        overageCents,
        totalCents: baseCents + overageCents,
    };
}
/** The effective price per message, in dollars, for the plans table. */
export function perMessageDollars(priceCents, includedMessages) {
    return priceCents / 100 / includedMessages;
}
// ---------------------------------------------------------------------------
// Warnings (section 3.5): informational only, never blocking
// ---------------------------------------------------------------------------
export const USAGE_FLAGS = ['ok', 'approaching_cap', 'over_cap', 'upgrade_candidate'];
/** `used so far ÷ days elapsed × days billed`, over the billed part of the cycle. */
export function projectMessages(used, window, at) {
    const total = daysInclusive(window.start, window.end);
    const elapsed = Math.min(total, Math.max(1, daysInclusive(window.start, isoOf(at.getTime()))));
    return Math.round((used / elapsed) * total);
}
export function usageWarnings(plan, founding, used, projected, options = {}, plans = PLANS) {
    const higher = nextPlanUp(plan.id, plans);
    const current = lineAmounts(plan, founding, projected, options);
    let upgrade = null;
    // Only a paid plan has overage that a bigger plan could undercut.
    if (higher !== null && !plan.stopsAtIncluded) {
        const asMonthly = { billedDays: options.billedDays, periodDays: options.periodDays };
        const mine = lineAmounts(plan, founding, projected, asMonthly);
        const next = lineAmounts(higher, founding, projected, asMonthly);
        if (next.totalCents < mine.totalCents) {
            upgrade = {
                plan: higher,
                projectedCostCents: mine.totalCents,
                nextPlanCostCents: next.totalCents,
            };
        }
    }
    return {
        approachingCap: used >= current.includedMessages * 0.8,
        overCap: used >= current.includedMessages,
        upgrade,
    };
}
/** The one flag a usage listing shows; the most urgent wins. */
export function usageFlag(warnings) {
    if (warnings.upgrade !== null)
        return 'upgrade_candidate';
    if (warnings.overCap)
        return 'over_cap';
    if (warnings.approachingCap)
        return 'approaching_cap';
    return 'ok';
}
/** What one discount takes off `amountCents`: never more than the amount, never negative. */
export function discountOff(rule, amountCents) {
    if (amountCents <= 0)
        return 0;
    const off = rule.kind === 'percent'
        ? Math.round((amountCents * (rule.percentOff ?? 0)) / 100)
        : (rule.amountOffCents ?? 0);
    return Math.min(amountCents, Math.max(0, off));
}
/**
 * The one discount each website gets in a cycle: the newest that covers it,
 * its own or the whole invoice's (user, 2026-10-21). `discounts` is oldest
 * first. A website with a discount is billed at the list price, never the
 * founding price, and no other discount applies to it.
 */
export function winningDiscounts(websiteIds, discounts) {
    const winners = new Map();
    for (const websiteId of websiteIds) {
        for (let i = discounts.length - 1; i >= 0; i--) {
            const rule = discounts[i];
            if (rule.websiteIds === null || rule.websiteIds.includes(websiteId)) {
                winners.set(websiteId, rule);
                break;
            }
        }
    }
    return winners;
}
/**
 * Applies an account's discounts to one cycle's lines, which must already be
 * priced at the list price for every website with a discount
 * (`winningDiscounts`). Each website gets only its winning discount: its own
 * comes off its line; a whole-invoice discount comes once off the lines it
 * won, added up. Nothing goes below zero.
 */
export function applyDiscounts(lines, discounts) {
    const winners = winningDiscounts(lines.map((line) => line.websiteId), discounts);
    const applied = [];
    const lineDiscountCents = new Map();
    for (const line of lines) {
        const rule = winners.get(line.websiteId);
        const off = rule && rule.websiteIds !== null ? discountOff(rule, line.totalCents) : 0;
        if (off > 0)
            applied.push({ discountId: rule.id, websiteId: line.websiteId, amountCents: off });
        lineDiscountCents.set(line.websiteId, off);
    }
    for (const rule of discounts) {
        if (rule.websiteIds !== null)
            continue;
        const won = lines.filter((line) => winners.get(line.websiteId) === rule);
        const off = discountOff(rule, won.reduce((sum, line) => sum + line.totalCents, 0));
        if (off > 0)
            applied.push({ discountId: rule.id, websiteId: null, amountCents: off });
    }
    const subtotal = lines.reduce((sum, line) => sum + line.totalCents, 0);
    const discountCents = applied.reduce((sum, a) => sum + a.amountCents, 0);
    return { lineDiscountCents, applied, discountCents, totalCents: subtotal - discountCents };
}
/** "20% off" or "$25.00 off" (per website when it covers websites). */
export function describeDiscount(rule) {
    if (rule.kind === 'percent')
        return `${rule.percentOff ?? 0}% off`;
    const each = rule.websiteIds !== null && rule.websiteIds.length > 1 ? ' each website' : '';
    return `${formatCents(rule.amountOffCents ?? 0)} off${each}`;
}
/** `INV-000123` from an invoice's number. */
export function invoiceNumberLabel(n) {
    return `INV-${String(n).padStart(6, '0')}`;
}
/** The least a card payment takes: $1.00. A smaller invoice is paid another way. */
export const MIN_CARD_PAYMENT_CENTS = 100;
/** "$1,234.50" from cents. */
export function formatCents(cents) {
    const dollars = Math.floor(Math.abs(cents) / 100).toLocaleString('en-US');
    const rest = String(Math.abs(cents) % 100).padStart(2, '0');
    return `${cents < 0 ? '−' : ''}$${dollars}.${rest}`;
}
//# sourceMappingURL=billing.js.map